import { DndContext, DragEndEvent, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { LoadingButton } from '@mui/lab';
import {
  Alert,
  Box,
  Button,
  CardContent,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Skeleton,
} from '@mui/material';
import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useActiveClassroom, useActiveSimulation } from '../../active-simulation/useActiveSimulation';
import { useStartClassroomSimulation } from '../../active-simulation/useStartSimulation';
import { useCancelClassroom, useClassroom, useUpdatePosition } from '../../api';
import AppBar from '../../app-bar/AppBar';
import { useCheckRole } from '../../auth';
import BackLink from '../../back-link/BackLink';
import Breadcrumbs from '../../breadcrumbs/Breadcrumbs';
import ClassroomCard from '../../classroom-card/ClassroomCard';
import ClassroomStudents from '../../classroom-students/ClassroomStudents';
import EditablePosition from '../../editable-position/EditablePosition';
import { apiPaths, authHeader } from '../../queryClient';
import SimulationErrorDialog from '../../simulation-error-dialog/SimulationErrorDialog';
import SimulationSkeleton from '../../skeletons/SimulationSkeleton';
import { ClassroomStudent as IClassroomStudent, LoadingSimulation, SimulationRole } from '../../types';
import { isInstructorClassroom } from '../../utils/activeSimulation';
import { useGetErrorMessage } from '../../utils/useGetErrorMessage';
import Vessel from '../../vessel/Vessel';
import { useSignalRClassroom } from './useSignalRClassroom';

/**
 * Simulation detail page from the instructor's point of view.
 *
 * This page is only for classroom cloud sessions, because those are the only type of simulations that an instructor starts FOR students.
 *
 * The page is visible as long as the simulation is not completed/abandoned.
 * When the classroom session is over, this page isn't relevant anymore and the instructor can see the student simulations on a different route/component.
 */
const Classroom = () => {
  useCheckRole(['Instructor']);

  const { exerciseId } = useParams();
  const { activeClassroom, isLoadingActiveClassroom } = useActiveClassroom();
  const isHostingThisClassroom = isInstructorClassroom(activeClassroom) && exerciseId === activeClassroom.exercise.id;

  const { classroom, classroomError, classroomLoading } = useClassroom(exerciseId, isHostingThisClassroom);

  const { updatePosition } = useUpdatePosition(exerciseId);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [showPositionDialog, setShowPositionDialog] = useState(false);
  const navigate = useNavigate();

  const sensors = useSensors(
    useSensor(KeyboardSensor),
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5, // Dragging is initiated after moving the mouse 5px
      },
    }),
  );

  const { cancelClassroom, isCancellingClassroom, cancelClassroomError } = useCancelClassroom(
    exerciseId,
    async () => checkForActiveClassroom(),
    () => {
      setShowCancelDialog(false);
      setShowCancelError(true);
    },
  );

  // We need this check(recall active api) because the response from delete Classroom API
  // and Abandon API provide statuses Accepted(200) or Not found(404),
  // this doesn't mean that Classroom was completed(deleted).
  const checkForActiveClassroom = async () =>
    await fetch(`/api/${apiPaths.activeClassrooms}`, {
      headers: { ...(await authHeader()), 'Content-Type': 'application/json' },
      method: 'GET',
    }).then(({ status }) => (status === 200 || status === 404 ? checkForActiveClassroom() : cancelCompleted()));

  const cancelCompleted = () => {
    setShowCancelDialog(false);
    setShowCancelError(false);
    navigate('../');
  };

  const [cancelClassroomErrorMessage, showCancelError, setShowCancelError] = useGetErrorMessage(
    cancelClassroomError ?? undefined,
  );

  useSignalRClassroom();

  const { setActiveSimulation } = useActiveSimulation();

  const { startSimulation, simulationStarting, startSimulationError } = useStartClassroomSimulation({
    classroomId: activeClassroom?.id,
    onSuccess: data => {
      const {
        exercise: { id, name, type },
        simulator,
      } = classroom!;

      const loadingSimulation: LoadingSimulation = {
        exercise: { id, name, type },
        id: data.sessionId,
        message: `Launching ${name ?? 'exercise'}`,
        progress: 0,
        role: SimulationRole.Instructor,
        simulator: {
          type: simulator.type,
        },
      };

      setActiveSimulation(loadingSimulation);
    },
    onError() {
      setActiveSimulation(null);
    },
  });

  if (!isLoadingActiveClassroom && !isHostingThisClassroom) {
    /* eslint-disable no-console */
    console.warn(
      'There is no classroom session hosted for this exercise',
      'isLoadingActiveClassroom',
      isLoadingActiveClassroom,
      'isHostingThisClassroom',
      isHostingThisClassroom,
    );
    // TODO Refactoring to avoid 404 case
    // throw json(null, { status: 404, statusText: 'There is no classroom session hosted for this exercise' });
  }

  if (isLoadingActiveClassroom || classroomLoading) {
    return (
      <SimulationSkeleton
        breadcrumbs={[
          { label: 'Home', to: '/teach' },
          { to: '..', label: <Skeleton width={160} /> },
          { label: <Skeleton width={120} /> },
        ]}
      />
    );
  }

  if (classroomError || !classroom) {
    throw classroomError;
  }

  const {
    exercise: { adHoc, name: exerciseName, layouts, type: exerciseType, vessels },
    positions,
    students,
    simulator: { type: simulatorType },
    code,
  } = classroom;

  const availableStudents =
    students?.filter(
      /* Include students that are not assigned to any position*/
      student => !positions?.some(position => position.userId === student.id),
    ) ?? [];

  const vesselsWithPositions = vessels?.map(vessel => ({
    ...vessel,
    positions: positions?.filter(position => position.vesselId === vessel.id) ?? [],
  }));

  const allAssigned = positions?.every(position => position.userId) ?? false;

  const handleSelect = (positionId: string, userId?: string) => {
    const position = positions?.find(position => position.id === positionId);
    updatePosition({ positionId, layoutId: position?.layoutId, userId: userId || undefined });
  };

  const handleChangeLayout = (positionId: string, layoutId: string) => {
    const position = positions?.find(position => position.id === positionId);
    updatePosition({ positionId, layoutId: layoutId || undefined, userId: position?.userId });
  };

  const onDragEnd = ({ over, active }: DragEndEvent) => {
    if (over) {
      const positionId = over.id.toString();
      // If you drag a student back to the list, the id is 'students', so it will remove the assignment (see useUpdatePosition)
      const position = positions?.find(position => position.id === positionId);
      updatePosition({ positionId, layoutId: position?.layoutId, userId: active.id.toString() });
    }
  };

  const assignAll = () => {
    positions?.forEach(position => {
      if (!position.userId) {
        const student = availableStudents.shift();
        if (student) {
          updatePosition({ positionId: position.id, layoutId: position.layoutId, userId: student.id });
        }
      }
    });
  };

  const unassignAll = () => {
    positions?.forEach(position => {
      if (position.userId) {
        updatePosition({ positionId: position.id });
      }
    });
  };

  const onStartClick = () => {
    if (positions.every(position => !position.userId)) {
      setShowPositionDialog(true);
    } else {
      startSimulation();
    }
  };

  return (
    <>
      <AppBar title="Exercise" />
      <Breadcrumbs
        items={[{ label: 'Home', to: '/teach' }, { to: '..', label: exerciseName }, { label: 'Set up team' }]}
        sx={{ display: { xs: 'none', sm: 'flex' } }}
      />
      <Container sx={{ pb: 4 }}>
        <BackLink label="Back to exercise" sx={{ display: { xs: 'inline-block', sm: 'none' }, my: 2 }} to=".." />

        <ClassroomCard
          adHoc={adHoc}
          actions={
            <>
              <Button variant="outlined" onClick={() => setShowCancelDialog(true)}>
                Cancel
              </Button>
              <LoadingButton loading={simulationStarting} onClick={onStartClick} variant="contained">
                Start simulation
              </LoadingButton>
            </>
          }
          code={code}
          exerciseName={exerciseName}
          exerciseType={exerciseType}
          simulatorType={simulatorType}
        />

        <DndContext onDragEnd={onDragEnd} sensors={sensors}>
          <Box display="grid" gap={3} gridTemplateColumns={{ xs: '1fr', md: 'repeat(3, 1fr)' }}>
            <Box
              display="grid"
              gap={3}
              gridTemplateColumns={{ xs: '1fr', sm: '1fr 1fr' }}
              gridColumn={{ xs: '1 / 4', md: '1 / 3' }}
              gridRow={{ sm: 2, md: 1 }}
            >
              {vesselsWithPositions?.map((vessel, i) => (
                <Vessel name={vessel.name} image={vessel.image} key={i}>
                  {vessel.positions.map(position => {
                    const studentMap = availableStudents.reduce<Record<string, IClassroomStudent>>((acc, student) => {
                      acc[student.id] = student;
                      return acc;
                    }, {});

                    const assignedStudent = students.find(student => student.id === position.userId);
                    if (position.userId && assignedStudent) {
                      studentMap[position.userId] = assignedStudent;
                    }

                    const listItems = Object.values(studentMap);

                    return (
                      <CardContent key={position.id}>
                        <EditablePosition
                          assignedStudent={assignedStudent}
                          layouts={layouts}
                          listItems={listItems}
                          onAssign={userId => handleSelect(position.id, userId)}
                          onChangeLayout={layoutId => handleChangeLayout(position.id, layoutId)}
                          position={position}
                        />
                      </CardContent>
                    );
                  })}
                </Vessel>
              ))}
            </Box>

            <Box display={{ xs: 'none', sm: 'block' }} gridColumn={{ sm: '1/4', md: 3 }} gridRow="1">
              <ClassroomStudents
                allAssigned={allAssigned}
                onAssignAll={assignAll}
                onUnassignAll={unassignAll}
                students={availableStudents}
              />
            </Box>
          </Box>
        </DndContext>
      </Container>

      <Dialog
        fullWidth
        maxWidth="xs"
        open={showCancelDialog}
        onClose={() => {
          setShowCancelDialog(false);
          setShowCancelError(false);
        }}
      >
        <DialogTitle>Cancel simulation</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to cancel this simulation? Invited students will be notified and the simulation
            details will no longer be accessible.
          </DialogContentText>
        </DialogContent>
        {showCancelError && <Alert severity="error">{cancelClassroomErrorMessage}</Alert>}
        <DialogActions sx={{ justifyContent: 'space-between' }}>
          <Button
            onClick={() => {
              setShowCancelError(false);
            }}
            disabled={isCancellingClassroom}
          >
            Cancel
          </Button>
          <LoadingButton onClick={() => cancelClassroom()} loading={isCancellingClassroom} variant="contained">
            Cancel simulation
          </LoadingButton>
        </DialogActions>
      </Dialog>

      <Dialog open={showPositionDialog} fullWidth maxWidth="xs">
        <DialogTitle>Student position</DialogTitle>
        <DialogContent>
          <DialogContentText>Assign at least one student to a position</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowPositionDialog(false)} variant="contained">
            Ok
          </Button>
        </DialogActions>
      </Dialog>

      <SimulationErrorDialog error={startSimulationError ?? undefined} retry={startSimulation} />
    </>
  );
};

export default Classroom;
